import React, { useState, useMemo, useRef, useEffect } from "react";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Divider,
  Box,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

const CustomSelect = ({
  label,
  value,
  onChange,
  options,
  sx = { minWidth: 150, marginTop: 2 },
  MenuProps = {},
  size,
  fullWidth,
  error,
  helperText,
  disabled,
  required,
}) => {
  const [searchText, setSearchText] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const searchInputRef = useRef(null);
  const labelId = `custom-select-label-${label.replace(/\s+/g, "-")}`;

  const filteredOptions = useMemo(() => {
    if (!searchText.trim()) return options ?? [];
    const searchWords = searchText.toLowerCase().trim().split(/\s+/).filter(Boolean);
    return (options ?? []).filter((option) => {
      const optionName = String(option?.name ?? "").toLowerCase();
      return searchWords.every((word) => optionName.includes(word));
    });
  }, [options, searchText]);

  useEffect(() => {
    if (menuOpen && searchInputRef.current) {
      const input = searchInputRef.current.querySelector("input");
      if (input) {
        setTimeout(() => {
          input.focus();
          input.setSelectionRange(input.value.length, input.value.length);
        }, 10);
      } else {
        setTimeout(() => {
          if (searchInputRef.current) {
            const inp = searchInputRef.current.querySelector("input");
            if (inp) {
              inp.focus();
              inp.setSelectionRange(inp.value.length, inp.value.length);
            }
          }
        }, 80);
      }
    }
  }, [menuOpen]);

  const handleMenuOpen = () => {
    setMenuOpen(true);
    if (typeof MenuProps?.onOpen === "function") {
      MenuProps.onOpen();
    }
  };

  const handleMenuClose = () => {
    setSearchText("");
    setMenuOpen(false);
    if (typeof MenuProps?.onClose === "function") {
      MenuProps.onClose();
    }
  };

  const stopAll = (e) => {
    e.stopPropagation();
    if (typeof e.preventDefault === "function") {
      // don't prevent default for text input keystrokes
    }
  };

  const handleSearchKeyDown = (e) => {
    e.stopPropagation();
    if (e.key === "Escape") {
      handleMenuClose();
    }
  };

  return (
    <FormControl
      sx={sx}
      variant="outlined"
      size={size || "small"}
      fullWidth={fullWidth}
      error={error}
      disabled={disabled}
      required={required}
    >
      <InputLabel id={labelId}>{label}</InputLabel>
      <Select
        labelId={labelId}
        label={label}
        value={value}
        onChange={onChange}
        onOpen={handleMenuOpen}
        onClose={handleMenuClose}
        size={size || "small"}
        error={error}
        disabled={disabled}
        MenuProps={{
          ...MenuProps,
          autoFocus: false,
          disableAutoFocusItem: true,
          disableRestoreFocus: false,
          transitionDuration: 0,
          PaperProps: {
            ...MenuProps.PaperProps,
            style: {
              maxHeight: 320,
              width: 280,
              ...MenuProps.PaperProps?.style,
            },
            sx: {
              ...MenuProps.PaperProps?.sx,
              "& .MuiMenu-list": {
                paddingTop: 0,
                paddingBottom: 0,
              },
            },
          },
        }}
      >
        <MenuItem
          sx={{
            padding: 0,
            position: "sticky",
            top: 0,
            zIndex: 2,
            backgroundColor: "background.paper",
            cursor: "default",
            "&:hover": {
              backgroundColor: "background.paper",
            },
            "&.Mui-selected": {
              backgroundColor: "background.paper",
            },
            "&.Mui-selected:hover": {
              backgroundColor: "background.paper",
            },
            "&.Mui-focusVisible": {
              backgroundColor: "background.paper",
            },
          }}
          disableRipple
          disableTouchRipple
          role="none"
          tabIndex={-1}
          onClick={stopAll}
          onMouseDown={stopAll}
          onKeyDown={stopAll}
        >
          <Box sx={{ p: 1, width: "100%" }} ref={searchInputRef}>
            <TextField
              size="small"
              placeholder="Search..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onClick={stopAll}
              onMouseDown={stopAll}
              onKeyDown={handleSearchKeyDown}
              onFocus={(e) => {
                e.stopPropagation();
              }}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Divider />
        </MenuItem>

        {filteredOptions.length > 0 ? (
          filteredOptions.map((option) => (
            <MenuItem key={String(option.id)} value={option.id}>
              {option.name}
            </MenuItem>
          ))
        ) : (
          <MenuItem disabled sx={{ justifyContent: "center", opacity: 0.6 }}>
            No results found
          </MenuItem>
        )}
      </Select>
      {helperText && (
        <Box
          component="span"
          sx={{
            color: error ? "error.main" : "text.secondary",
            fontSize: "0.75rem",
            mt: "3px",
            ml: "14px",
            mr: "14px",
          }}
        >
          {helperText}
        </Box>
      )}
    </FormControl>
  );
};

export default CustomSelect;
